import { ProductItem } from ':cloud/types/ts_types';
import {
  ADVANCED_TRADING_DOCS_LINK,
  COINBASE_SIGNIN_DOCS_LINK,
  COINBASE_WALLET_SDK_DOCS_LINK,
  COMMERCE_DOCS_LINK,
  EXCHANGE_DOCS_LINK,
  ONRAMP_DOCS_LINK,
  PRIME_DOCS_LINK,
  ROSETTA_DOCS_LINK,
} from ':cloud/utils/links';
import { AppRoute } from ':cloud/utils/routes';

export enum ProductFilterCategory {
  ALL = 'All',
  STAKING = 'Staking',
  PAYMENTS = 'Payments',
  WALLETS = 'Wallets',
  OFF_PLATFORM = 'Off platform',
}

const UNINTEGRATED_PRODUCTS: ProductItem[] = [
  {
    navigationIcon: 'advancedTradeProduct',
    spotIconName: 'advancedTradeProduct',
    title: 'Advanced Trade API',
    description: 'Automate your trades programmatically on the most trusted crypto exchange.',
    linkToRoute: ADVANCED_TRADING_DOCS_LINK,
    isOffPlatform: true,
    categories: [],
  },
  {
    navigationIcon: 'primePoduct',
    spotIconName: 'primeProduct',
    title: 'Prime API',
    description: 'Use the API to trade, provide custody, and manage crypto assets.',
    linkToRoute: PRIME_DOCS_LINK,
    isOffPlatform: true,
    categories: [],
  },
  {
    navigationIcon: 'exchangeProduct',
    spotIconName: 'exchangeProduct',
    title: 'Exchange API',
    description: 'Use the API to place orders, track market data, and more.',
    linkToRoute: EXCHANGE_DOCS_LINK,
    isOffPlatform: true,
    categories: [],
  },
  {
    navigationIcon: 'payProduct',
    spotIconName: 'paySDKProduct',
    title: 'Coinbase Pay',
    iconName: 'payProduct',
    altDescription: 'Free',
    description: 'Enable a fiat to crypto on-ramp for your customers.',
    shortDesc: 'Integreate fiat on-ramp',
    linkToRoute: ONRAMP_DOCS_LINK,
    iconColor: 'secondaryForeground',
    isOffPlatform: true,
    categories: [ProductFilterCategory.PAYMENTS, ProductFilterCategory.OFF_PLATFORM],
  },
  {
    navigationIcon: 'commerceProduct',
    spotIconName: 'commerceProduct',
    title: 'Commerce',
    description: 'Accept and manage secure crypto payments.',
    linkToRoute: COMMERCE_DOCS_LINK,
    isOffPlatform: true,
    categories: [ProductFilterCategory.PAYMENTS, ProductFilterCategory.OFF_PLATFORM],
  },
  {
    navigationIcon: 'walletProduct',
    spotIconName: 'productWallet',
    title: 'Coinbase Wallet SDK',
    description: 'Allow users to access your dapp with Coinbase Wallet.',
    linkToRoute: COINBASE_WALLET_SDK_DOCS_LINK,
    isOffPlatform: true,
    categories: [ProductFilterCategory.WALLETS, ProductFilterCategory.OFF_PLATFORM],
  },
  {
    navigationIcon: 'signinProduct',
    spotIconName: 'signInProduct',
    title: 'Sign in with Coinbase',
    description: 'Enable users to log in with Coinbase.',
    linkToRoute: COINBASE_SIGNIN_DOCS_LINK,
    isOffPlatform: true,
    categories: [ProductFilterCategory.OFF_PLATFORM],
  },
  {
    navigationIcon: 'rosettaProduct',
    spotIconName: 'rosettaProduct',
    title: 'Rosetta',
    description: 'Enable simpler, faster, and more repliable blockchain integrations.',
    linkToRoute: ROSETTA_DOCS_LINK,
    isOffPlatform: true,
    categories: [ProductFilterCategory.OFF_PLATFORM],
  },
];

export const INTEGRATED_PRODUCTS: ProductItem[] = [
  {
    navigationIcon: 'base',
    spotIconName: 'nodeProduct',
    productType: 'base',
    title: 'Node',
    shortDesc: 'Read and write to Base with free, rate limited RPC endpoints',
    altDescription: 'Free RPC endpoint for Base',
    description: 'Read and write to Base with free, rate limited RPC endpoints',
    linkToRoute: AppRoute.Products.Base,
    createProjectLink: AppRoute.Projects.NewBase,
    hasButton: true,
    categories: [],
    productTag: 'Preview',
    isNew: true,
  },
  {
    navigationIcon: 'advancedTradeProduct',
    spotIconName: 'advancedTradeProduct',
    productType: 'rat',
    title: 'Advanced Trade',
    shortDesc: 'Automate your trades',
    description: 'Automate your trading & order management and obtain up-to-date market data',
    linkToRoute: AppRoute.Products.AdvancedTrade,
    categories: [],
  },
  {
    navigationIcon: 'waasProduct',
    spotIconName: 'walletAsAServiceProduct',
    iconName: 'waasProduct',
    title: 'Wallet as a Service',
    productType: 'waas',
    altDescription: 'Starts at $0.12/ wallet per mo.',
    description:
      'Build fully customizable wallets and provide users seamless access to web3 natively in your app.',
    shortDesc: 'Onboard your customers to web3',
    linkToRoute: AppRoute.Products.Waas,
    createProjectLink: AppRoute.Projects.New,
    hasButton: true,
    iconColor: 'primaryForeground',
    categories: [ProductFilterCategory.WALLETS],
  },
  {
    navigationIcon: 'waasProduct',
    spotIconName: 'walletAsAServiceProduct',
    iconName: 'waasProduct',
    title: 'Wallets',
    productType: 'waas_consumer',
    altDescription: '',
    description: 'Give your users access to wallets natively within your app',
    shortDesc: 'Onboard your customers to web3',
    linkToRoute: AppRoute.Products.Wallets,
    createProjectLink: AppRoute.Products.Wallets,
    hasButton: true,
    iconColor: 'primaryForeground',
    categories: [ProductFilterCategory.WALLETS],
    isNew: true,
  },
  {
    navigationIcon: 'staking',
    spotIconName: 'pieChart',
    title: 'Paymaster',
    productType: 'base',
    description: 'Sponsor gas on behalf of your users and send transactions from smart wallets',
    linkToRoute: AppRoute.Products.BundlerAndPaymaster,
    hasButton: true,
    categories: [],
    isNew: true,
  },
  {
    navigationIcon: 'staking',
    spotIconName: 'stakingProduct',
    title: 'Staking',
    productType: 'staking_api',
    altDescription: 'Free',
    description:
      'Simplify complex staking operations so that you can easily and quickly build staking products for your end users.',
    shortDesc: 'Maximize your customer rewards',
    linkToRoute: AppRoute.Products.Staking,
    createProjectLink: AppRoute.Products.Staking,
    hasButton: true,
    categories: [],
    isNew: true,
  },
  {
    navigationIcon: 'participateProduct',
    spotIconName: 'participateProduct',
    title: 'Participate',
    productType: 'participate',
    description:
      'Launch private, dedicated validators and staking nodes for your own funds or your customers.',
    shortDesc: 'Launch private validators and staking nodes',
    linkToRoute: AppRoute.Products.Participate,
    iconColor: 'secondaryForeground',
    productTag: 'Enterprise',
    hasButton: true,
    categories: [ProductFilterCategory.STAKING],
  },
  {
    navigationIcon: 'exchangeProduct',
    spotIconName: 'exchangeProduct',
    title: 'Exchange',
    productType: 'exchange',
    description:
      'An Institutional API for direct access to our spot exchange for trading and market data',
    shortDesc: 'Access our spot for trading and market data',
    linkToRoute: AppRoute.Products.Exchange,
    createProjectLink: AppRoute.Products.Exchange,
    hasButton: true,
    categories: [],
    isNew: true,
  },
];

export const ALL_PRODUCTS: ProductItem[] = [...INTEGRATED_PRODUCTS, ...UNINTEGRATED_PRODUCTS];
